/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useContext, useRef, useMemo, useId } from 'react'
import { updateOrderStatus } from '../../../helpers/api'
import {
    Segment,
    Grid,
    Image,
    Icon,
    Placeholder,
    Message,
    Button, Loader, List, Label,
} from 'semantic-ui-react'
import formatDate from '../../../helpers/formatDate'
import { UltyModalWrapperContext } from '../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import {
    acceptOrder, addEventToOrder,
    denyOrder,
    getOrder,
    postPickingProof,
} from '../../../services/Order/Order.service'
import { useTranslation } from 'react-i18next'
import OrderDenyReasonMessage from '../OrderDenyReason/OrderDenyReasonMessage'
import { OrderDetailContext } from './OrderDetailContext'
import { useNavigate, useParams } from 'react-router-dom-v5-compat'
import OrderEvents from '../OrderEvents/OrderEvents'
import RequestCourier from './RequestCouriers'
import UltyPopup from '../../Shared/UltyPopup/UltyPopup'
import CancelOrder from './CancelOrder'
import useBreadcrumb from '../../NavBar/useBreadcrumb'
import useUser from '../../Shared/UserProvider/useUser'
import { OrderItemsList, orderItemsListDisplayMode } from './OrderItemList/OrderItemsList'
import OrderFlowActions from './OrderFlowActions/OrderFlowActions'
import OrderPOSDelivery from './OrderPOSDelivery'
import { isProviderAPos } from '../../../services/Provider/Provider.service'
import { PrintOrderButton } from '../../../printing/components/PrintButton/PrintOrderButton'
import { getClientName } from '../../../helpers/order'
import { ScanningInformationLabel, useScanning } from '../../../scanning/scanning.service'
import { useCamera } from '../../../camera/useCamera'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AskToAddPickingProof } from '../PickingProof/AskToAddPickingProof'
import { NavigateToPickingProofsGalleryButton } from '../PickingProof/NavigateToPickingProofsGalleryButton'
import { OrderStatusBar } from './OrderStatusBar'
import { OrderPdfPrintButton } from './OrderPdfPrint/OrderPdfPrintButton'
import { OrderImagePrintButton } from './OrderImagePrint/OrderImagePrintButton'
import { useDeviceMediaQuery } from '../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { ItemsAwaitingActionButton } from './ItemsAwaitingActionButton'
import { RemoveOrderItemsReminderMessage } from './RemoveOrderItemsReminderMessage'
import OrderPickupIncomplete from './OrderScanPicking/OrderPickupIncomplete'
import { getSorter } from '../../../helpers/array'
import { Couriers } from './Courier/Couriers'
import { usePickingPath } from '../../Admin/AdminMerchant.js/PickingPath/usePickingPath'
import OrderPickupRemovedItemRemainder
    from './OrderItemList/OrderPickupRemovedItemRemainder/OrderPickupRemovedItemRemainder'
import { isAfter } from 'date-fns'
import { addHours } from 'date-fns'

import { getOrderItemWithPickingAction } from './_helpers/orderItemFilter'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'

const PHONES = {
    'ubereats': '0805 084 286',
    'deliveroo': '09 77 55 03 31',
    'justeat': '01 86 65 46 15',
}

const PHONES_COUNTRIES = {
    'FR': PHONES,
    'BE': {
        'ubereats': '+32 2 808 66 28',
        'deliveroo': '+32 2 895 40 06 (FR) / +32 2 895 40 07 (NL)',
        'justeat': '',
    },
}

const MAX_PICKING_PROOFS = 5

const getPhones = (countryCode) => {
    return PHONES_COUNTRIES[countryCode] ?? PHONES
}

const createAislePositionMap = (pickingPath) => {
    if (!pickingPath) return new Map()

    const aislePositionMap = new Map()
    let position = 0

    pickingPath.path.forEach(parentAisle => {
        parentAisle.aisles.forEach(childAisle => {
            aislePositionMap.set(childAisle.id, position++)
        })
    })

    return aislePositionMap
}

const getOrderItemPosition = (orderItem, aislePositionMap) => {
    if (orderItem.fulfillmentAction === 'CANCEL_ORDER') {
        return '-'
    }

    const aisleId = orderItem.item?.inventory?.aisleId

    if (aisleId && aislePositionMap.has(aisleId)) {
        return aislePositionMap.get(aisleId)
    }

    return orderItem.categoryName ?? orderItem.id.toString()
}

const orderDetailSorter = (pickingPath) => {
    const aislePositionMap = createAislePositionMap(pickingPath)
    return getSorter(orderItem => getOrderItemPosition(orderItem, aislePositionMap))
}

const OrderDetail = () => {
    const [t] = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const id = +params.id
    const {setPaths} = useBreadcrumb()
    const [isDenying, setIsDenying] = useState(false)
    const [isDenyAcceptLoading, setIsDenyAcceptLoading] = useState(false)
    const {user} = useUser()
    const orderItemListRef = useRef(null)
    const {isMobile, isDesktop} = useDeviceMediaQuery()
    const [displayMode, setDisplayMode] = useState(isMobile ? orderItemsListDisplayMode.PRODUCTS : orderItemsListDisplayMode.LINES)
    const queryClient = useQueryClient()
    const orderStatus = queryClient.getQueryData(['orders', id])?.status

    const scrollToOrderItemList = () => {
        if (orderItemListRef.current) {
            const navbarHeight = 60
            const element = orderItemListRef.current
            const y = element.getBoundingClientRect().top + window.pageYOffset - navbarHeight
            window.scrollTo({top: y, behavior: 'smooth'})
        }
    }

    function getOrderUpdateMessage(o) {
        let message = `${t('order.detail.updated_by_other_device')}`
        if (!o.isModifiable) {
            message += ` ${t('order.detail.order_is_not_modifiable_anymore')}`
        }
        return message
    }

    const {pickingPath} = usePickingPath()

    const {data: order, isLoading, isSuccess} = useQuery({
        queryFn: async () => {
            const o = await getOrder(id)

            const previousOrder = queryClient.getQueryData(['orders', id])
            if (previousOrder && previousOrder.status !== o.status) {
                const message = getOrderUpdateMessage(o)
                ultyToast({ type: 'success', title: message })
            }

            if (window.Android && window.Android.acknowledgeOrder) {
                window.Android.acknowledgeOrder(o.id)
            }
            return o
        },
        refetchInterval: 10000,
        queryKey: ['orders', id],
        enabled: !orderStatus || ['RECEIVED', 'ACCEPTED', 'PREPARING', 'READY'].includes(orderStatus),
        select: (order) => {
            order.orderItems.sort(orderDetailSorter(pickingPath))
            return order
        },
    })

    useEffect(() => {
        if (isSuccess && order?.orderItems.every(orderItem => orderItem.pickingMethod)) {
            window.scrollTo({top: 0, behavior: 'smooth'})
        }
    }, [isSuccess, order?.orderItems])

    const changeOrderStatusMutation = useMutation({
        mutationFn: ({status}) => updateOrderStatus(id, status),
        onSuccess: async (data, {status}) => {
            if (data.error && data.error === 'CANCELLED') {
                ultyToast({ type: 'error', title: t('order.error_cancelled') })
            }

            if (['ACCEPTED', 'PREPARING'].includes(status)) {
                scrollToOrderItemList()
            }

            queryClient.setQueryData(['orders', id], (prevData) => ({
                ...prevData,
                status,
            }))
            await queryClient.invalidateQueries({queryKey: ['orders', id]})
        },
    })

    const {isInReplacement} = useContext(OrderDetailContext)

    useQuery({
        queryKey: ['orders', id, 'events'],
        queryFn: () => addEventToOrder(id, 'STATUS_VIEWED'),
        enabled: order?.status === 'RECEIVED',
    })

    useEffect(() => {
        if (window.Android && window.Android.stopNotifications) {
            window.Android.stopNotifications()
        }
    }, []);

    const canScan = useMemo(() => order?.isModifiable || false, [order?.isModifiable])

    useScanning(() => {
        if (order?.isModifiable && !isInReplacement) {
            if (['RECEIVED', 'ACCEPTED'].includes(order?.status)) {
                changeOrderStatusMutation.mutate({status: 'PREPARING'})
            }
            setDisplayMode(orderItemsListDisplayMode.PRODUCTS)
        }
    },canScan)

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.orders'),
            link: true,
            path: `/orders/${isProviderAPos(user.provider) ? 'current' : 'finished'}`,
        }, {
            text: order?.brand?.name || t('global.loading'),
            link: false,
        }, {
            text: order ? `${order.platform.name} - ${order.displayId}` : t('global.loading'),
            link: false,
        }])
    }, [order])

    const shouldDisplayTimer = useMemo(() => {
        return !!order
          && (
            order.status === 'RECEIVED'
            || (
              (order.status === 'ACCEPTED' || order.status === 'PREPARING')
              && order.platform.displayPreparationTimer)
          )
    }, [order])

    const postPickingProofMutation = useMutation({
        mutationFn: ({orderId, pickingProof}) => postPickingProof(orderId, pickingProof),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ['orders', id]})
        },
    })

    const cameraId = useId()
    const {takePhoto, isCameraAvailable} = useCamera((image) => {
        postPickingProofMutation.mutate({orderId: order.id, pickingProof: image})
    }, cameraId)

    const switchInDenyMode = () => {
        setIsDenying(!isDenying)
    }

    const {handleUltyModalWrapper} = React.useContext(UltyModalWrapperContext)
    const canTakePickingProof = isCameraAvailable() && (order?.pickingProofs || []).length < MAX_PICKING_PROOFS


    const isPickupIncomplete = () => {
        const hasOneHourPassedSincePickupAt = !!order.pickupAt
          && isAfter(new Date(), addHours(new Date(order.pickupAt), 1))

        return order.orderItems
            .some(orderItem => !orderItem.pickingMethod && !orderItem.fulfillmentIssue)
          && !hasOneHourPassedSincePickupAt
    }

    const showPickupIncompleteModal = () => {
        const orderItemsNotPicked = order.orderItems
          .filter(orderItem => !orderItem.pickingMethod && !orderItem.fulfillmentIssue)


        if (orderItemsNotPicked.length > 0 && isPickupIncomplete()) {
            handleUltyModalWrapper(true, {
                closeIcon: undefined,
                component: <OrderPickupIncomplete
                  orderItems={orderItemsNotPicked}
                  onSubmit={() => {
                      const firstItemNotPicked = document.getElementById(`orderItem-${orderItemsNotPicked[0].id}`)
                      if (firstItemNotPicked) {
                          firstItemNotPicked.scrollIntoView({behavior: 'smooth'})
                      }
                  }}
                />,
            })
        }
    }


    const hasSomeRemovedItems = () => getOrderItemWithPickingAction(order.orderItems).length > 0

    const showRemoveItemModal = () => {
        const orderItemsWithPotentialAction = getOrderItemWithPickingAction(order.orderItems)

        if (orderItemsWithPotentialAction.length > 0) {
            handleUltyModalWrapper(true, {
                closeIcon: undefined,
                component: <OrderPickupRemovedItemRemainder
                  orderItems={order.orderItems}
                  onSubmit={() => {
                      handleMarkAsReadyOrder('ignore_removed_items_check')
                  }}
                />,
            })
        }
    }


    const handleMarkAsReadyOrder = async (ignoreCheck = undefined) => {
        try {
            setIsDenyAcceptLoading(true)

            if (isPickupIncomplete()) {
                showPickupIncompleteModal()
                return
            }

            if (hasSomeRemovedItems() && ignoreCheck !== 'ignore_removed_items_check') {
                showRemoveItemModal()
                return
            }

            if (canTakePickingProof) {
                const remainingPickingProofs = MAX_PICKING_PROOFS - order.pickingProofs.length
                const getAskToAddPickingProofModalSettings = ({isFirstAsk, remainingPickingProofs}) => {
                    return {
                        closeIcon: undefined,
                        component: <AskToAddPickingProof
                          orderId={order.id}
                          remainingPickingProofs={remainingPickingProofs}
                          isFirstAsk={isFirstAsk}
                          afterSubmit={async (action) => {
                              if (action === 'CLOSE_AND_CONTINUE' || !canTakePickingProof || remainingPickingProofs <= 1) {
                                  handleUltyModalWrapper(false, null)
                                  changeOrderStatusMutation.mutate({status: 'READY'})
                              } else {
                                  handleUltyModalWrapper(true, getAskToAddPickingProofModalSettings({
                                      isFirstAsk: false,
                                      remainingPickingProofs: remainingPickingProofs - 1,
                                  }))
                              }
                          }}/>,
                    }
                }
                handleUltyModalWrapper(true, getAskToAddPickingProofModalSettings({
                    isFirstAsk: true,
                    remainingPickingProofs,
                }))
            } else {
                await changeOrderStatusMutation.mutateAsync({status: 'READY'})
            }
        } catch (e) {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        } finally {
            setIsDenyAcceptLoading(false)
        }
    }

    const handleAcceptOrder = async (delay) => {
        try {
            setIsDenyAcceptLoading(true)
            const acceptedOrder = await acceptOrder(order, delay)
            scrollToOrderItemList()
            queryClient.setQueryData(['orders', id], acceptedOrder)
            await queryClient.invalidateQueries({queryKey: ['orders', id]})
            setIsDenyAcceptLoading(false)
        } catch (e) {
            console.log(e)
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        } finally {
            setIsDenyAcceptLoading(false)
        }
    }

    const handleDenyReasonSubmit = async (denyReason) => {
        setIsDenyAcceptLoading(true)
        const deniedOrder = await denyOrder(order, {type: denyReason.type.name, details: denyReason.details})
        queryClient.setQueryData(['orders', id], () => deniedOrder)
        setIsDenying(false)
        setIsDenyAcceptLoading(false)
    }

    const handleUpdateCourier = async (order) => {
        const modalSettings = {
            title: `${t('order.couriers.title')}`,
            component: <RequestCourier order={order} afterSubmit={async () => {
                await queryClient.invalidateQueries({queryKey: ['orders', id]})
                handleUltyModalWrapper(false)
            }}/>,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const handleCancelOrder = async (order) => {
        const modalSettings = {
            title: `${t('order.cancel.header')}`,
            component: <CancelOrder order={order} afterSubmit={async () => {
                await queryClient.invalidateQueries({queryKey: ['orders', id]})
                handleUltyModalWrapper(false)
            }}/>,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const handleShowPickingProofGallery = () => {
        navigate(`picking-proofs`)
    }

    const handleShowHistory = async () => {
        const modalSettings = {
            component: <OrderEvents orderId={order.id} afterSubmit={async () => {
                handleUltyModalWrapper(false)
            }}/>,
        }
        handleUltyModalWrapper(true, modalSettings)
    }

    const getClientPhone = (client) => {
        try {
            const m = (client || '').match(/^([^()]*) \((.*)\)$/)
            if (m) {
                return m[2]
            }
        } catch {
        }

        return client || 'N/C'
    }

    const isOrderLoading = isLoading || changeOrderStatusMutation.isPending

    if (!order) {
        return <Loader active/>
    }

    const receivedOrderLifetime = order?.receivedOrderLifetime || order?.platform.receivedOrderLifetime || (order?.platform.module === 'ubereats' && 60 * 6)

    return (
      <>
          <Loader active={isOrderLoading}/>
          {order &&
            <>
                {order.status === 'CANCELLED' || order.status === 'EXPIRED' ? (
                  <Message
                    icon="times"
                    header={t(`order.status.${order.status}`)}
                    content={order.status === 'CANCELLED' ? t('order.cancelled') : t('order.expired')}
                    error
                  />
                ) : (
                  <Segment loading={isOrderLoading} basic css={css`
                      ${isMobile && 'padding: 0 !important;'}
                  `}>
                      {shouldDisplayTimer &&
                        <OrderFlowActions
                          order={order}
                          orderLifetime={receivedOrderLifetime}
                          isDenying={isDenying}
                          isDenyAcceptLoading={isDenyAcceptLoading}
                          onDenyOrder={switchInDenyMode}
                          onMarkAsReady={handleMarkAsReadyOrder}
                          onAcceptOrder={handleAcceptOrder}
                          onDenyReasonSubmit={handleDenyReasonSubmit}
                        />
                      }

                      {order.status === 'DENIED' &&
                        <OrderDenyReasonMessage order={order}/>
                      }

                      <OrderStatusBar
                        status={order.status}
                        onStatusChange={({status}) => {
                            if (['READY', 'COMPLETED'].includes(status) && isPickupIncomplete()) {
                                showPickupIncompleteModal()
                            } else {
                                changeOrderStatusMutation.mutate({status})
                            }
                        }}
                      />

                      {!['COMPLETED', 'DENIED', 'EXPIRED'].includes(order.status) &&
                        <RemoveOrderItemsReminderMessage orderItems={order.orderItems}/>
                      }

                      {order.orderItems.some(orderItem => orderItem.customerAckType === 'AWAITING_ACTION') && (
                        <ItemsAwaitingActionButton
                          orderItemListRef={orderItemListRef}
                          orderItems={order.orderItems}
                        />
                      )}
                  </Segment>
                )}

                <Segment
                  basic={isMobile}
                  loading={isOrderLoading}
                >
                    {order.isModifiable && <ScanningInformationLabel/>}
                    <>
                        <Grid
                          css={isMobile && css`
                              &.ui.grid > .column:not(.row) {
                                  padding: 0;
                              }
                          `}
                        >
                            <Grid.Column width={10}>
                                {!isMobile && (
                                  <Button.Group>
                                      <Button
                                        icon
                                        active={displayMode === orderItemsListDisplayMode.LINES}
                                        onClick={() => setDisplayMode(orderItemsListDisplayMode.LINES)}>
                                          <Icon name="th list"/>
                                      </Button>
                                      <Button
                                        icon
                                        active={displayMode === orderItemsListDisplayMode.PRODUCTS}
                                        onClick={() => setDisplayMode(orderItemsListDisplayMode.PRODUCTS)}>
                                          <Icon name="th"/>
                                      </Button>
                                  </Button.Group>
                                )}
                            </Grid.Column>
                            <Grid.Column width={6} textAlign="right">
                                <PrintOrderButton order={order}/>
                                {!isMobile && order.pickingProofs.length > 0 &&
                                  <NavigateToPickingProofsGalleryButton
                                    css={css`
                                        &.ui.button {
                                            margin-bottom: .5rem;
                                        }
                                    `}
                                    order={order}
                                  />}
                                {isDesktop && order.status === 'COMPLETED' && <OrderImagePrintButton order={order}/>}
                                {order && order.status && (
                                  <UltyPopup items={[
                                      ...(!['CANCELLED', 'DENIED', 'RECEIVED', 'EXPIRED'].includes(order.status) ? [{
                                          icon: 'times',
                                          color: 'red',
                                          label: t('order.cancel.button'),
                                          onClick: () => handleCancelOrder(order),
                                          active: false,
                                      }] : []),
                                      ...(!['CANCELLED', 'DENIED', 'RECEIVED', 'COMPLETED', 'EXPIRED'].includes(order.status) ? [{
                                          icon: 'shipping fast',
                                          color: 'teal',
                                          label: t('order.couriers.button'),
                                          onClick: () => handleUpdateCourier(order),
                                          active: order.platform.module === 'ubereats' || order.platform.module === 'ulty',
                                      }, {
                                          icon: 'camera',
                                          color: 'teal',
                                          label: t('order.picking_proof.take_picking_proof'),
                                          onClick: () => {
                                              takePhoto(cameraId)
                                          },
                                          active: canTakePickingProof,
                                      }] : []),
                                      {
                                          icon: 'image outline',
                                          color: 'teal',
                                          label: t('order.picking_proof.download_x_picking_proofs', {count: order.pickingProofs.length}),
                                          onClick: handleShowPickingProofGallery,
                                          active: isMobile && order.pickingProofs.length > 0,
                                      },
                                      {
                                          icon: 'history',
                                          color: 'teal',
                                          label: t('order.history.header'),
                                          onClick: handleShowHistory,
                                          active: true,
                                      },
                                  ]}/>
                                )}
                            </Grid.Column>
                        </Grid>

                        <Grid>
                            {!isMobile && (
                              <Grid.Column width={3}>
                                  {isOrderLoading ? (
                                    <Placeholder>
                                        <Placeholder.Image square/>
                                    </Placeholder>
                                  ) : (
                                    <Image src={order.platform.logo}/>
                                  )}
                              </Grid.Column>
                            )}
                            <Grid.Column tablet={12} mobile={16} computer={12} css={css`
                                display: flex !important;
                                flex-direction: column;
                                padding: ${isMobile ? '0' : '1rem'} !important;

                                label {
                                    font-weight: bold;
                                }
                            `}>
                                <div css={css`
                                    display: flex;
                                `}>
                                    {isMobile && (
                                      <div css={css`
                                          display: flex;
                                          flex-direction: column;
                                      `}>
                                          {isOrderLoading ? (
                                            <Placeholder>
                                                <Placeholder.Image square/>
                                            </Placeholder>
                                          ) : (
                                            <Image size="tiny" src={order.platform.logo}/>
                                          )}
                                          <div css={css`
                                              font-weight: bold;
                                              text-align: center;
                                              font-size: 1.2em;
                                              padding-top: 5px;
                                          `}>{order.displayId}</div>
                                      </div>
                                    )}
                                    <List relaxed css={css`
                                        flex: 1;
                                        ${isMobile ? 'padding-left: 1em !important;' : ''}
                                        margin-top: 0 !important;

                                        &.ui.list > .item {
                                            display: flex;
                                            align-items: center;
                                        }

                                        div.header {
                                            color: #4183c4 !important;
                                    `}>
                                        <List.Item>
                                            <List.Icon name="dolly" size="large" verticalAlign="middle"/>
                                            <List.Content>
                                                <List.Header>{t('order.type')}: {order.orderType ? t(`order.type_${order.orderType}`) : 'N/C'}</List.Header>
                                            </List.Content>
                                        </List.Item>
                                        <List.Item>
                                            <List.Icon name="hourglass half" size="large" verticalAlign="middle"/>
                                            <List.Content>
                                                <List.Header>{t('order.pickup_at')}: {order.pickupAt ? formatDate(order.pickupAt) : 'N/C'}</List.Header>
                                            </List.Content>
                                        </List.Item>
                                        <List.Item>
                                            <List.Icon name="user" size="large"/>
                                            <List.Content>
                                                <List.Header>
                                                    {getClientName(order.customer)}
                                                    <span css={css`margin-left: 1rem`}>
                                                            {order.customerOrderCount === 1 && (
                                                              <Label color="yellow" size="mini">
                                                                  <Icon name="thumbs up"/>
                                                                  {t(isMobile ? 'order.detail.first_order_mobile' : 'order.detail.first_order')}
                                                              </Label>
                                                            )}

                                                        {order.customerOrderCount > 1 && (
                                                          <Label color="green" size="mini">
                                                              <Icon name="rocket"/>
                                                              {t(isMobile ? 'order.detail.nth_order_mobile' : 'order.detail.nth_order', {count: order.customerOrderCount})}
                                                          </Label>
                                                        )}
                                                                </span>
                                                </List.Header>
                                            </List.Content>
                                        </List.Item>
                                        <List.Item>
                                            <List.Icon name="phone" size="large" verticalAlign="middle"/>
                                            <List.Content>
                                                <List.Header>{getClientPhone(order.customer)}</List.Header>
                                            </List.Content>
                                        </List.Item>
                                    </List>
                                </div>

                                {['ubereats', 'deliveroo', 'justeat'].includes(order.platform.module) && (
                                  <Message icon compact
                                           css={css`
                                               &.ui.message {
                                                   margin-bottom: 0;
                                               }
                                           `}
                                  >
                                      <Icon name="phone volume"/>
                                      <Message.Content>
                                          <Message.Header css={css`
                                              text-align: center;
                                          `}>{`${t('order.hotline')} - ${order.platform.name}`}</Message.Header>
                                          <div css={css`
                                              font-weight: bold;
                                              text-align: center;
                                              font-size: 1.14285714em;
                                          `}>{getPhones(user.getCompanyCountry())[order.platform.module]}{order.platform.module === 'deliveroo' && order.platform.storeId ? ` - Restaurant ID : ${order.platform.storeId}` : ''}</div>
                                      </Message.Content>
                                  </Message>
                                )}
                                {order.isModifiable && ['justeat'].includes(order.platform.module) && (
                                  <Message icon compact warning>
                                      <Icon name="warning volume"/>
                                      <Message.Content>
                                          <Message.Header css={css`
                                              text-align: center;
                                          `}>
                                              {`${t('order.modify_limited_support.title', {platformName: order.platform.name})}`}
                                          </Message.Header>
                                          <div css={css`
                                              text-align: center;
                                          `}>{t('order.modify_limited_support.message', {platformName: order.platform.name})}</div>
                                      </Message.Content>
                                  </Message>
                                )}

                                {order.orderType === 'DELIVERY' &&
                                  <Couriers
                                    couriers={order.couriersInfo || []}
                                    expectedCouriers={order.couriers}
                                    orderStatus={orderStatus}
                                  />
                                }

                                {order.orderType === 'POS_DELIVERY' && (
                                  <OrderPOSDelivery orderId={order.id}/>
                                )}

                                <OrderItemsList
                                  displayMode={displayMode}
                                  order={order}
                                  orderItems={order.orderItems}
                                  orderItemListRef={orderItemListRef}
                                />

                                <div css={css`
                                    display: flex;
                                    flex-direction: ${isMobile ? 'column' : 'row'};
                                `}>
                                    <div css={css` flex: 1;
                                        white-space: pre-wrap;
                                        margin-top: ${isMobile ? '10px' : 0}`}>
                                        <Icon name="sticky note"/>{t('order.notes')}:
                                        <div css={css` padding: 4px; `}>
                                            {(order.notes || '') || 'N/C'}
                                        </div>
                                    </div>
                                </div>
                            </Grid.Column>
                        </Grid>
                    </>
                </Segment>
            </>
          }
      </>
    )
}

export default OrderDetail
